import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    pannellum: any;
  }
}

// Track whether the Pannellum CDN assets are already injected into <head>
// so multiple instances on the same page don't double-load.
let cssInjected = false;
let jsPromise: Promise<void> | null = null;

function loadPannellum(): Promise<void> {
  if (jsPromise) return jsPromise;

  if (!cssInjected) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
    document.head.appendChild(link);
    cssInjected = true;
  }

  jsPromise = new Promise<void>((resolve, reject) => {
    if (window.pannellum) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Pannellum'));
    document.head.appendChild(script);
  });

  return jsPromise;
}

export interface PannellumViewerProps {
  /** Equirectangular panorama image URL */
  src: string;
  /** Auto-rotate speed in degrees/second. Negative = clockwise. Default -6. */
  autoRotate?: number;
  /** Allow the user to drag/interact. Default true. */
  draggable?: boolean;
  /** Horizontal field of view in degrees. Default 100. */
  hfov?: number;
  /** Initial pitch. Default 0. */
  pitch?: number;
  /** Initial yaw. Default 0. */
  yaw?: number;
  className?: string;
  /** Shown while the panorama is loading */
  loadingNode?: React.ReactNode;
  /** Shown if Pannellum fails to load */
  errorNode?: React.ReactNode;
}

const PannellumViewer: React.FC<PannellumViewerProps> = ({
  src,
  autoRotate = -6,
  draggable = true,
  hfov = 100,
  pitch = 0,
  yaw = 0,
  className = '',
  loadingNode,
  errorNode,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);

  useEffect(() => {
    let destroyed = false;

    loadPannellum()
      .then(() => {
        if (destroyed || !containerRef.current || !window.pannellum) return;

        try {
          viewerRef.current = window.pannellum.viewer(containerRef.current, {
            type: 'equirectangular',
            panorama: src,
            autoLoad: true,
            autoRotate,
            autoRotateInactivityDelay: 1500,
            compass: false,
            showControls: false,
            showFullscreenCtrl: false,
            showZoomCtrl: false,
            mouseZoom: false,
            keyboardZoom: false,
            draggable,
            friction: 0.15,
            hfov,
            minHfov: 50,
            maxHfov: 130,
            pitch,
            yaw,
          });

          viewerRef.current.on('load', () => {
            if (loadingRef.current) loadingRef.current.style.display = 'none';
            if (errorRef.current) errorRef.current.style.display = 'none';
          });

          viewerRef.current.on('error', () => {
            if (loadingRef.current) loadingRef.current.style.display = 'none';
            if (errorRef.current) errorRef.current.style.display = 'flex';
          });
        } catch (e) {
          console.error('Pannellum init error:', e);
          if (loadingRef.current) loadingRef.current.style.display = 'none';
          if (errorRef.current) errorRef.current.style.display = 'flex';
        }
      })
      .catch(() => {
        if (loadingRef.current) loadingRef.current.style.display = 'none';
        if (errorRef.current) errorRef.current.style.display = 'flex';
      });

    return () => {
      destroyed = true;
      if (viewerRef.current?.destroy) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [src, autoRotate, draggable, hfov, pitch, yaw]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Pannellum mounts here */}
      <div ref={containerRef} className="w-full h-full" />

      {/* Loading overlay */}
      <div
        ref={loadingRef}
        className="absolute inset-0 flex items-center justify-center bg-maintenance-navy/80 text-white/60 text-sm pointer-events-none"
      >
        {loadingNode ?? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            Loading 360° view…
          </span>
        )}
      </div>

      {/* Error overlay — hidden until needed */}
      <div
        ref={errorRef}
        className="absolute inset-0 hidden items-center justify-center bg-maintenance-navy/80 text-white/50 text-sm"
      >
        {errorNode ?? '360° view unavailable'}
      </div>
    </div>
  );
};

export default PannellumViewer;
